import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n";
import { getActiveOrgId } from "../../utils/orgUtils";
import { buildCloudRequestUrl } from "../integrations/gcpDiscoverySession.mjs";
import { GovernanceListingSection } from "../governance/GovernanceListingSection";
import { ComplianceCenterMetricTile } from "./ComplianceCenterMetricTile";
import { Button } from "../ui/Button";
import { SearchField } from "../ui/SearchField";
import { Select } from "../ui/Select";
import listStyles from "@/styles/pages/governance/GovernanceListing.module.scss";

type CheckFact = { protocol?: string | null; ports?: string[]; sources?: string[] };

type CheckPresentation = {
  facts?: CheckFact[];
  targets?: string[] | null;
  targetServiceAccounts?: string[] | null;
  targetsNote?: string;
  denyDoesNotProveOverride?: boolean;
  reachabilityAssessed?: boolean;
  confirmedFinding?: boolean;
  evaluated?: boolean;
};

type CheckRow = {
  id: string;
  ruleTitle: string;
  ruleSummary?: string | null;
  ruleId: string;
  status: string;
  severity?: string | null;
  reason?: string | null;
  resourceName: string;
  externalId: string;
  coverage?: { complete?: boolean; limitations?: string[] };
  remediation?: string | null;
  observed?: Record<string, unknown>;
  presentation?: CheckPresentation;
  evaluatedAt?: string | null;
  collectedAt?: string | null;
  storageStatus?: string | null;
  evidenceState?: string | null;
  evidenceId?: string | null;
  findingInstanceId?: string | null;
  permission?: { permission?: string } | null;
};

type ChangeRow = {
  id: string;
  resourceName: string;
  externalId: string;
  field: string | null;
  before: unknown;
  after: unknown;
  at?: string | null;
  source?: string;
};

type BaselineSummary = {
  resources?: number;
  kinds?: Array<{ kind: string; count: number }>;
  formedAt?: string | null;
  latestAt?: string | null;
};

type BaselineRow = {
  id: string;
  resourceName: string;
  externalId: string;
  kind?: string | null;
  location?: string | null;
  connection?: string | null;
  at?: string | null;
};

type Summary = {
  scopeNote?: string;
  connections?: number;
  syncRuns?: number;
  checks?: { FAIL?: number; PASS?: number; NOT_EVALUATED?: number };
  connectionsList?: Array<{ id: string; displayName: string; projectId: string; lastRunStatus?: string | null }>;
};

function textValue(value: unknown) {
  if (value == null) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function localTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function statusClass(status: string) {
  if (status === "FAIL") return listStyles.badgeMissed;
  if (status === "PASS") return listStyles.badgeCompleted;
  if (status === "NOT_EVALUATED") return listStyles.badgePlanned;
  return listStyles.badgeDefault;
}

function severityClass(severity?: string | null) {
  if (severity === "CRITICAL" || severity === "HIGH") return listStyles.badgeMissed;
  if (severity === "MEDIUM") return listStyles.badgePlanned;
  return listStyles.badgeDefault;
}

function reasonText(t: (key: string, fallback?: string, params?: Record<string, string | number>) => string, reason?: string | null) {
  if (!reason) return t("gcpChecks.reasonMissing", "The required data was not collected.");
  const known = t(`gcpChecks.reasons.${reason}`, "");
  if (known && known !== `gcpChecks.reasons.${reason}`) return known;
  return t("gcpChecks.reasonGeneric", "The check stopped because this data is missing: {{reason}}.", { reason });
}

function CopyLine({ label, value }: { label: string; value?: string | null }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <p>
      <span className={listStyles.cellMuted}>{label}</span>
      {" "}
      <code>{value}</code>
      {" "}
      <Button type="button" size="small" color="outline" onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        }).catch(() => setCopied(false));
      }}>{copied ? t("gcpChecks.copied", "Copied") : t("gcpChecks.copy", "Copy")}</Button>
    </p>
  );
}

function CheckDetails({
  selected,
  onClose,
  evidenceHref,
}: {
  selected: CheckRow;
  onClose: () => void;
  evidenceHref: string;
}) {
  const { t } = useI18n();
  const presentation = selected.presentation || {};
  const facts = Array.isArray(presentation.facts) ? presentation.facts : [];
  const notEvaluated = selected.status === "NOT_EVALUATED";
  const targets = Array.isArray(presentation.targets) ? presentation.targets : [];
  const accounts = Array.isArray(presentation.targetServiceAccounts) ? presentation.targetServiceAccounts : [];
  return (
    <aside>
      <h3>{selected.ruleTitle}</h3>
      {selected.ruleSummary ? <p>{selected.ruleSummary}</p> : null}
      <p>{t("gcpChecks.resource", "Resource")}: {selected.resourceName}</p>
      <p>
        {t("gcpChecks.severity", "Severity")}: {selected.severity || "—"}
        {" · "}
        {t("gcpChecks.result", "Result")}: {notEvaluated ? t("gcpChecks.notEvaluated", "Not evaluated") : selected.status === "FAIL" ? t("gcpChecks.fail", "Fail") : selected.status}
        {" · "}
        {t("gcpChecks.time", "Time")}: {localTime(selected.collectedAt || selected.evaluatedAt)}
        {" · "}
        {t("gcpChecks.coverage", "Coverage")}: {selected.coverage?.complete ? t("gcpChecks.complete", "Complete") : t("gcpChecks.limited", "Limited")}
      </p>
      {notEvaluated ? (
        <>
          <p>{t("gcpChecks.notVulnerability", "This check was not evaluated. It is not a confirmed vulnerability or finding.")}</p>
          <p>{t("gcpChecks.gapReason", "Why data is missing")}: {reasonText(t, selected.reason)}</p>
          {selected.permission?.permission ? <p>{t("gcpChecks.permissionNeeded", "Required permission")}: {selected.permission.permission}</p> : null}
          <p>{t("gcpChecks.neededAction", "What is needed")}: {selected.remediation || t("gcpChecks.reasonMissing", "The required data was not collected.")}</p>
        </>
      ) : (
        <>
          <h4>{t("gcpChecks.facts", "Observed facts")}</h4>
          {facts.length === 0 ? <p>{t("gcpChecks.noFacts", "No protocol, port, or source was recorded for this result.")}</p> : (
            <ul>
              {facts.map((fact, index) => (
                <li key={`${fact.protocol || "protocol"}-${index}`}>
                  {fact.protocol || "—"} / {t("gcpChecks.ports", "Ports")} {(fact.ports || []).join(", ") || "—"} / {t("gcpChecks.sources", "Source")} {(fact.sources || []).join(", ") || "—"}
                </li>
              ))}
            </ul>
          )}
          <p>
            {t("gcpChecks.targets", "Targets")}: {
              presentation.targetsNote === "TARGET_TAGS" || targets.length > 0
                ? targets.join(", ")
                : presentation.targetsNote === "TARGET_SERVICE_ACCOUNTS" || accounts.length > 0
                  ? accounts.join(", ")
                  : presentation.targetsNote === "NO_TARGET_RESTRICTION_RECORDED"
                    ? t("gcpChecks.targetsEmpty", "No target tags or service accounts are recorded on this rule. That is not a checked list of VMs, and reachability was not evaluated.")
                    : t("gcpChecks.targetsMissing", "Targets were not included in this observation.")
            }
          </p>
          <p>{t("gcpChecks.reachability", "The rule configuration was checked. Actual network reachability was not evaluated.")}</p>
          {presentation.denyDoesNotProveOverride ? <p>{t("gcpChecks.denyNote", "A deny entry is present. It does not prove that the allow is overridden.")}</p> : null}
          <p>{t("gcpChecks.recommendation", "Recommendation")}: {selected.remediation || "—"}</p>
        </>
      )}
      <p>
        {selected.evidenceId ? <Link to={evidenceHref}>{t("gcpChecks.openEvidence", "Evidence")}</Link> : t("gcpChecks.noEvidence", "No stored file")}
        {" · "}
        {selected.findingInstanceId ? (
          <span>{t("gcpChecks.linkedTask", "Linked remediation task")}: {selected.findingInstanceId}</span>
        ) : (
          <span>{t("gcpChecks.noSpecificTask", "No remediation task is linked to this check.")}</span>
        )}
        {" · "}
        <Link to="/compliance-center/remediation">{t("gcpChecks.generalRemediation", "Remediation workspace (general)")}</Link>
      </p>
      <details>
        <summary>{t("gcpChecks.technical", "Technical data")}</summary>
        <CopyLine label={t("gcpChecks.reasonCode", "Reason code")} value={selected.reason} />
        <CopyLine label={t("gcpChecks.ruleId", "Rule id")} value={selected.ruleId} />
        <CopyLine label={t("gcpChecks.externalId", "External id")} value={selected.externalId} />
        <CopyLine label={t("gcpChecks.evidenceId", "Evidence id")} value={selected.evidenceId} />
        <CopyLine label={t("gcpChecks.findingId", "Finding id")} value={selected.findingInstanceId} />
        <p className={listStyles.cellMuted}>{t("gcpChecks.observedJson", "Observed JSON. A boolean in this payload is not a reachability verdict.")}</p>
        <CopyLine label={t("gcpChecks.observedJson", "Observed JSON")} value={JSON.stringify(selected.observed || {})} />
      </details>
      <Button type="button" size="small" color="outline" onClick={onClose}>{t("common.close", "Close")}</Button>
    </aside>
  );
}

export default function CloudMonitoringPanels({ token }: { token: string }) {
  const { t } = useI18n();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [checks, setChecks] = useState<CheckRow[]>([]);
  const [changes, setChanges] = useState<ChangeRow[]>([]);
  const [baselineSummary, setBaselineSummary] = useState<BaselineSummary | null>(null);
  const [baselineRows, setBaselineRows] = useState<BaselineRow[]>([]);
  const [baselineCursor, setBaselineCursor] = useState<string | null>(null);
  const [baselineOpen, setBaselineOpen] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [status, setStatus] = useState("PROBLEMS");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<CheckRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = () => {
      const orgId = getActiveOrgId();
      if (!token || !orgId) {
        setSummary(null);
        setChanges([]);
        setBaselineSummary(null);
        setBaselineRows([]);
        setBaselineOpen(false);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const base = import.meta.env.VITE_API_URL || "/api";
      void fetch(buildCloudRequestUrl(base, "/summary", orgId), { headers }).then(async (response) => {
        if (!active || getActiveOrgId() !== orgId) return;
        const body = await response.json().catch(() => ({}));
        setSummary(response.ok ? body : null);
      }).catch(() => {
        if (active && getActiveOrgId() === orgId) setSummary(null);
      });
      void fetch(buildCloudRequestUrl(base, "/config-drift", orgId), { headers }).then(async (response) => {
        if (!active || getActiveOrgId() !== orgId) return;
        const body = await response.json().catch(() => ({}));
        setChanges(response.ok && Array.isArray(body.changes) ? body.changes : []);
        setBaselineSummary(response.ok ? body.baselineSummary || null : null);
        setBaselineRows([]);
        setBaselineCursor(null);
        setBaselineOpen(false);
      }).catch(() => {
        if (active && getActiveOrgId() === orgId) {
          setChanges([]);
          setBaselineSummary(null);
        }
      });
    };
    load();
    window.addEventListener("ds-active-organization-changed", load);
    return () => {
      active = false;
      window.removeEventListener("ds-active-organization-changed", load);
    };
  }, [token]);

  useEffect(() => {
    let active = true;
    const orgId = getActiveOrgId();
    if (!token || !orgId) {
      setChecks([]);
      return undefined;
    }
    const headers = { Authorization: `Bearer ${token}` };
    const checkPath = `/check-results?status=${encodeURIComponent(status)}&q=${encodeURIComponent(query)}`;
    void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || "/api", checkPath, orgId), { headers }).then(async (response) => {
      if (!active || getActiveOrgId() !== orgId) return;
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(t("gcpChecks.unavailable", "Configuration checks are not available."));
        setChecks([]);
        return;
      }
      setError(null);
      setChecks(Array.isArray(body.results) ? body.results : []);
      setCursor(typeof body.nextCursor === "string" ? body.nextCursor : null);
    }).catch(() => {
      if (active && getActiveOrgId() === orgId) setChecks([]);
    });
    return () => {
      active = false;
    };
  }, [query, status, t, token]);

  const loadMore = () => {
    const orgId = getActiveOrgId();
    if (!token || !orgId || !cursor) return;
    const headers = { Authorization: `Bearer ${token}` };
    const path = `/check-results?status=${encodeURIComponent(status)}&q=${encodeURIComponent(query)}&cursor=${encodeURIComponent(cursor)}`;
    void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || "/api", path, orgId), { headers }).then(async (response) => {
      if (getActiveOrgId() !== orgId) return;
      const body = await response.json().catch(() => ({}));
      const incoming = Array.isArray(body.results) ? body.results : [];
      setChecks((current) => {
        const seen = new Set(current.map((row) => row.id));
        return [...current, ...incoming.filter((row: CheckRow) => !seen.has(row.id))];
      });
      setCursor(typeof body.nextCursor === "string" ? body.nextCursor : null);
    }).catch(() => undefined);
  };

  const loadBaselines = (nextCursor?: string | null) => {
    const orgId = getActiveOrgId();
    if (!token || !orgId) return;
    const headers = { Authorization: `Bearer ${token}` };
    const path = `/config-baselines${nextCursor ? `?cursor=${encodeURIComponent(nextCursor)}` : ""}`;
    void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || "/api", path, orgId), { headers }).then(async (response) => {
      if (getActiveOrgId() !== orgId) return;
      const body = await response.json().catch(() => ({}));
      const incoming = response.ok && Array.isArray(body.baselines) ? body.baselines : [];
      setBaselineRows((current) => {
        const base = nextCursor ? current : [];
        const seen = new Set(base.map((row) => row.id));
        return [...base, ...incoming.filter((row: BaselineRow) => !seen.has(row.id))];
      });
      setBaselineCursor(typeof body.nextCursor === "string" ? body.nextCursor : null);
      setBaselineOpen(true);
    }).catch(() => undefined);
  };

  const kindSummary = (baselineSummary?.kinds || []).map((item) => `${item.kind} ${item.count}`).join(" · ");
  const evidenceHref = (row: CheckRow) => `/compliance-center/evidence?evidenceSource=GCP_CONFIGURATION&ruleId=${encodeURIComponent(row.ruleId)}&externalId=${encodeURIComponent(row.externalId)}`;

  return (
    <>
      <GovernanceListingSection
        title={t("gcpSummary.title", "Google Cloud")}
        description={summary?.scopeNote || t("gcpSummary.scope", "These counts are Google Cloud connections and sync runs. Workflow connectors above have a different scope.")}
      >
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid6}`}>
          <ComplianceCenterMetricTile label={t("gcpSummary.connections", "Cloud connections")} value={summary?.connections || 0} tone="green" />
          <ComplianceCenterMetricTile label={t("gcpSummary.runs", "Cloud sync runs")} value={summary?.syncRuns || 0} tone="purple" />
          <ComplianceCenterMetricTile label={t("gcpChecks.fail", "Fail")} value={summary?.checks?.FAIL || 0} tone="red" />
          <ComplianceCenterMetricTile label={t("gcpChecks.pass", "Pass")} value={summary?.checks?.PASS || 0} tone="blue" />
          <ComplianceCenterMetricTile label={t("gcpChecks.notEvaluated", "Not evaluated")} value={summary?.checks?.NOT_EVALUATED || 0} tone="yellow" />
        </div>
        <ul>
          {(summary?.connectionsList || []).map((row) => (
            <li key={row.id}>
              <Link to="/integrations/google-cloud/connection">{row.displayName || row.projectId}</Link>
              {row.lastRunStatus ? ` · ${row.lastRunStatus}` : ""}
            </li>
          ))}
        </ul>
      </GovernanceListingSection>
      <GovernanceListingSection
        title={t("gcpChecks.title", "Google Cloud configuration checks")}
        description={t("gcpChecks.intro", "A check result is separate from evidence storage. A pass is not compliance with a whole control.")}
      >
        <div className={listStyles.filtersRow}>
          <SearchField label={t("common.search", "Search")} value={query} onChange={setQuery} />
          <Select
            label={t("gcpChecks.status", "Status")}
            value={status}
            onChange={(value) => setStatus(String(value || "PROBLEMS"))}
            options={[
              { value: "PROBLEMS", label: t("gcpChecks.problems", "Problems and gaps") },
              { value: "FAIL", label: t("gcpChecks.fail", "Fail") },
              { value: "NOT_EVALUATED", label: t("gcpChecks.notEvaluated", "Not evaluated") },
              { value: "PASS", label: t("gcpChecks.pass", "Pass") },
              { value: "ALL", label: t("gcpChecks.all", "All") },
            ]}
          />
        </div>
        {error ? <p>{error}</p> : null}
        {checks.length === 0 ? (
          <p className={listStyles.emptyState}>{t("gcpChecks.empty", "No configuration check results for this organization.")}</p>
        ) : (
          <div className={listStyles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>{t("gcpChecks.rule", "Check")}</th>
                  <th>{t("gcpChecks.resource", "Resource")}</th>
                  <th>{t("gcpChecks.status", "Result")}</th>
                  <th>{t("gcpChecks.severity", "Severity")}</th>
                  <th>{t("gcpChecks.coverage", "Coverage")}</th>
                  <th>{t("gcpChecks.observed", "Observed")}</th>
                  <th>{t("gcpChecks.storage", "Evidence storage")}</th>
                  <th>{t("gcpChecks.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((row) => (
                  <tr key={row.id}>
                    <td>{row.ruleTitle}</td>
                    <td title={row.externalId}>{row.resourceName}</td>
                    <td><span className={`${listStyles.badge} ${statusClass(row.status)}`}>{row.status === "NOT_EVALUATED" ? t("gcpChecks.notEvaluated", "Not evaluated") : row.status === "FAIL" ? t("gcpChecks.fail", "Fail") : row.status === "PASS" ? t("gcpChecks.pass", "Pass") : row.status}</span></td>
                    <td>{row.severity ? <span className={`${listStyles.badge} ${severityClass(row.severity)}`}>{row.severity}</span> : "—"}</td>
                    <td>{row.coverage?.complete ? t("gcpChecks.complete", "Complete") : t("gcpChecks.limited", "Limited")}</td>
                    <td>{localTime(row.collectedAt || row.evaluatedAt)}</td>
                    <td>
                      {row.evidenceState === "NOT_FORMED"
                        ? t("gcpChecks.notFormed", "Not formed: the check was not evaluated")
                        : row.evidenceState === "FAILED"
                          ? <span className={`${listStyles.badge} ${listStyles.badgeMissed}`}>{t("gcpChecks.storageFailed", "Storage failed")}</span>
                          : row.evidenceState === "STORED"
                            ? <span className={`${listStyles.badge} ${listStyles.badgeCompleted}`}>{t("gcpChecks.stored", "Stored")}</span>
                            : t("gcpChecks.noEvidence", "No stored file")}
                    </td>
                    <td>
                      <div className={listStyles.actionsGap}>
                        <Button type="button" size="small" color="outline" onClick={() => setSelected(row)}>{t("gcpChecks.details", "Details")}</Button>
                        <Link to={evidenceHref(row)}>{t("gcpChecks.openEvidence", "Evidence")}</Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cursor ? <Button type="button" size="small" color="outline" onClick={loadMore}>{t("gcpIntegration.loadMore", "Load more")}</Button> : null}
        {selected ? (
          <CheckDetails selected={selected} evidenceHref={evidenceHref(selected)} onClose={() => setSelected(null)} />
        ) : null}
      </GovernanceListingSection>
      <GovernanceListingSection
        title={t("gcpDrift.title", "Configuration drift")}
        description={t("gcpDrift.intro", "The first complete snapshot is a baseline. A missing observation is not a deletion.")}
      >
        {(baselineSummary?.resources || 0) > 0 ? (
          <div>
            <p>
              {t("gcpDrift.summary", "{{count}} resources with a baseline", { count: baselineSummary?.resources || 0 })}
              {kindSummary ? ` · ${kindSummary}` : ""}
              {baselineSummary?.formedAt ? ` · ${t("gcpDrift.formed", "Formed")} ${localTime(baselineSummary.formedAt)}` : ""}
              {baselineSummary?.latestAt && baselineSummary.latestAt !== baselineSummary.formedAt ? ` – ${localTime(baselineSummary.latestAt)}` : ""}
            </p>
            <Button type="button" size="small" color="outline" onClick={() => (baselineOpen ? setBaselineOpen(false) : loadBaselines())}>
              {baselineOpen ? t("gcpDrift.hide", "Hide baseline") : t("gcpDrift.view", "View baseline")}
            </Button>
          </div>
        ) : null}
        {baselineOpen ? (
          baselineRows.length === 0 ? (
            <p className={listStyles.emptyState}>{t("gcpDrift.emptyBaseline", "No baseline snapshots for this organization.")}</p>
          ) : (
            <div className={listStyles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>{t("gcpDrift.name", "Name")}</th>
                    <th>{t("gcpDrift.kind", "Kind")}</th>
                    <th>{t("gcpDrift.location", "Location")}</th>
                    <th>{t("gcpDrift.connection", "Connection")}</th>
                    <th>{t("gcpDrift.captured", "Snapshot time")}</th>
                  </tr>
                </thead>
                <tbody>
                  {baselineRows.map((row) => (
                    <tr key={row.id}>
                      <td title={row.externalId}>{row.resourceName}</td>
                      <td>{row.kind || "—"}</td>
                      <td>{row.location || "—"}</td>
                      <td>{row.connection || "—"}</td>
                      <td>{localTime(row.at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {baselineCursor ? <Button type="button" size="small" color="outline" onClick={() => loadBaselines(baselineCursor)}>{t("gcpIntegration.loadMore", "Load more")}</Button> : null}
            </div>
          )
        ) : null}
        {changes.length === 0 ? (
          <p className={listStyles.emptyState}>{t("gcpDrift.empty", "No configuration drift for this organization.")}</p>
        ) : (
          <div className={listStyles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>{t("gcpChecks.resource", "Resource")}</th>
                  <th>{t("gcpDrift.field", "Field")}</th>
                  <th>{t("gcpDrift.before", "Before")}</th>
                  <th>{t("gcpDrift.after", "After")}</th>
                  <th>{t("gcpDrift.when", "When")}</th>
                  <th>{t("gcpDrift.source", "Source")}</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((row) => (
                  <tr key={row.id}>
                    <td title={row.externalId}>{row.resourceName}</td>
                    <td>{row.field || "—"}</td>
                    <td>{textValue(row.before)}</td>
                    <td>{textValue(row.after)}</td>
                    <td>{localTime(row.at)}</td>
                    <td>{row.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GovernanceListingSection>
    </>
  );
}
